import { push } from 'connected-react-router'
import { differenceInCalendarDays } from 'date-fns'
import { omit } from 'lodash'
import { call, put, race, select, take } from 'redux-saga/effects'

import {
  api,
  checkoutOrder,
  loadCreditCards,
  resetCaches,
  submitCoupon,
  submitCredit,
} from '../../api/api'
import { confirmationRoute } from '../../routes/routes'
import {
  logException,
  normalizeApplyCouponErrorResponse,
  normalizeErrorResponse,
} from '../../util/errorUtils'
import { formatUrl } from '../../util/formatUtils'
import { pushAnalyticsEvent, pushGTMDataLayer } from '../../util/gtmUtils'
import { loadCheckoutCartStart } from './cart'
import { updatePersonalInfoStart } from './profile'
import { apiSaga, formSaga } from './sagas'
import {
  selectCouponUsage,
  selectCurrentDeliveryDropoff,
  selectLocationDeliveryId,
  selectOrderTotal,
} from './selectors'
import { resetSubscriptionState } from './subscriptions'

// ------------------------------------
// CONSTs
// ------------------------------------
const CHECKOUT_ERROR = 'There was an error checking out.'
const HEARTLAND_ERROR =
  'Your network is preventing us from loading a necessary resource. Please email us at support@foodsby.com.'
export const CREDIT_CARDS_ERROR = 'There was an error loading your saved credit cards.'

// ------------------------------------
// Action Types & Creators
// ------------------------------------
export const CHECKOUT = 'foodsby/checkout/CHECKOUT'
export const CHECKOUT_SUCCESS = 'foodsby/checkout/CHECKOUT_SUCCESS'
export const CHECKOUT_FAILURE = 'foodsby/checkout/CHECKOUT_FAILURE'
export const CLEAR_ERROR = 'foodsby/checkout/CLEAR_ERROR'
export const APPLY_COUPON = 'foodsby/checkout/APPLY_COUPON'
export const APPLY_COUPON_SUCCESS = 'foodsby/checkout/APPLY_COUPON_SUCCESS'
export const APPLY_COUPON_FAILURE = 'foodsby/checkout/APPLY_COUPON_FAILURE'
export const APPLY_CREDIT = 'foodsby/checkout/APPLY_CREDIT'
export const APPLY_CREDIT_SUCCESS = 'foodsby/checkout/APPLY_CREDIT_SUCCESS'
export const APPLY_CREDIT_FAILURE = 'foodsby/checkout/APPLY_CREDIT_FAILURE'
export const LOAD_CREDIT_CARDS = 'foodsby/checkout/LOAD_CREDIT_CARDS'
export const LOAD_CREDIT_CARDS_SUCCESS = 'foodsby/checkout/LOAD_CREDIT_CARDS_SUCCESS'
export const LOAD_CREDIT_CARDS_FAILURE = 'foodsby/checkout/LOAD_CREDIT_CARDS_FAILURE'
export const CLOSE_AUTO_FAVORITE_MODAL = 'foodsby/checkout/CLOSE_AUTO_FAVORITE_MODAL'
export const LOAD_AVAILABLE_PROGRAM = 'foodsby/checkout/LOAD_AVAILABLE_PROGRAM'
export const LOAD_AVAILABLE_PROGRAM_SUCCESS = 'foodsby/checkout/LOAD_AVAILABLE_PROGRAM_SUCCESS'
export const LOAD_AVAILABLE_PROGRAM_FAILURE = 'foodsby/checkout/LOAD_AVAILABLE_PROGRAM_FAILURE'

export const checkoutStart = checkoutData => {
  return {
    payload: { checkoutData },
    type: CHECKOUT,
  }
}

export const checkoutSuccess = orderInfo => {
  return {
    payload: { orderInfo },
    type: CHECKOUT_SUCCESS,
  }
}

export const checkoutFailure = error => {
  return {
    error,
    type: CHECKOUT_FAILURE,
  }
}

export function clearError() {
  return {
    type: CLEAR_ERROR,
  }
}

export const applyCredit = (creditAmount, skipDeliveryCalc) => {
  return {
    payload: { creditAmount, skipDeliveryCalc },
    type: APPLY_CREDIT,
  }
}

export const applyCreditSuccess = () => {
  return {
    type: APPLY_CREDIT_SUCCESS,
  }
}

export const applyCreditFailure = error => {
  return {
    error,
    type: APPLY_CREDIT_FAILURE,
  }
}

export const applyCoupon = (couponCode, skipDeliveryCalc) => {
  return {
    payload: { couponCode, skipDeliveryCalc },
    type: APPLY_COUPON,
  }
}

export const applyCouponSuccess = () => {
  return {
    type: APPLY_COUPON_SUCCESS,
  }
}

export const applyCouponFailure = error => {
  return {
    error,
    type: APPLY_COUPON_FAILURE,
  }
}

export const loadCreditCardsStart = () => {
  return {
    type: LOAD_CREDIT_CARDS,
  }
}

export const loadCreditCardsSuccess = creditCards => {
  return {
    payload: { creditCards },
    type: LOAD_CREDIT_CARDS_SUCCESS,
  }
}

export const loadCreditCardsFailure = error => {
  return {
    error,
    type: LOAD_CREDIT_CARDS_FAILURE,
  }
}

export const closeAutoFavorite = () => ({
  type: CLOSE_AUTO_FAVORITE_MODAL,
})

export const loadAvailableProgram = date => ({
  payload: { date },
  type: LOAD_AVAILABLE_PROGRAM,
})

export const loadAvailableProgramSuccess = program => ({
  payload: { program },
  type: LOAD_AVAILABLE_PROGRAM_SUCCESS,
})

export const loadAvailableProgramFailure = error => ({
  error,
  type: LOAD_AVAILABLE_PROGRAM_FAILURE,
})

// ------------------------------------
// Action Handlers
// ------------------------------------

const ACTION_HANDLERS = {
  [APPLY_COUPON]: state => {
    return {
      ...state,
      isCouponApplying: true,
    }
  },
  [APPLY_COUPON_FAILURE]: (state, action) => {
    const error = normalizeApplyCouponErrorResponse(action.error)

    return {
      ...state,
      couponError: error,
      isCouponApplying: false,
    }
  },
  [APPLY_COUPON_SUCCESS]: state => {
    return {
      ...state,
      couponError: undefined,
      isCouponApplying: false,
    }
  },
  [APPLY_CREDIT]: state => {
    return {
      ...state,
      isCreditApplying: true,
    }
  },
  [APPLY_CREDIT_FAILURE]: (state, action) => {
    const error = normalizeErrorResponse(action.error)

    return {
      ...state,
      creditError: error,
      isCreditApplying: false,
    }
  },
  [APPLY_CREDIT_SUCCESS]: state => {
    return {
      ...state,
      creditError: undefined,
      isCreditApplying: false,
    }
  },
  [CHECKOUT]: state => {
    return {
      ...state,
      checkoutError: undefined,
      isCheckoutProcessing: true,
    }
  },
  [CHECKOUT_FAILURE]: (state, action) => {
    const message = normalizeErrorResponse(action.error, CHECKOUT_ERROR)
    return {
      ...state,
      checkoutError: {
        message,
      },
      isCheckoutProcessing: false,
    }
  },
  [CHECKOUT_SUCCESS]: (state, action) => {
    const orderInfo = action.payload.orderInfo
    return {
      ...state,
      checkoutError: undefined,
      favoriteInfo: orderInfo.favoriteInfo,
      isCheckoutProcessing: false,
      orderId: orderInfo.orderId,
    }
  },
  [CLEAR_ERROR]: state => {
    return {
      ...state,
      checkoutError: undefined,
    }
  },
  [CLOSE_AUTO_FAVORITE_MODAL]: state => ({
    ...state,
    favoriteInfo: undefined,
  }),
  [LOAD_AVAILABLE_PROGRAM]: state => ({
    ...state,
    isLoadingProgram: true,
    loadingProgramError: undefined,
  }),
  [LOAD_AVAILABLE_PROGRAM_FAILURE]: (state, action) => ({
    ...state,
    isLoadingProgram: false,
    loadingProgramError: action.error,
    programInfo: undefined,
  }),
  [LOAD_AVAILABLE_PROGRAM_SUCCESS]: (state, action) => ({
    ...state,
    isLoadingProgram: false,
    loadingProgramError: undefined,
    programInfo: action.payload.program,
  }),
  [LOAD_CREDIT_CARDS]: state => {
    return {
      ...state,
      checkoutError: undefined,
      couponError: undefined,
      creditCardsError: undefined,
      creditError: undefined,
      isCreditCardsLoading: true,
    }
  },
  [LOAD_CREDIT_CARDS_FAILURE]: state => {
    return {
      ...state,
      creditCards: [],
      creditCardsError: CREDIT_CARDS_ERROR,
      isCreditCardsLoading: false,
    }
  },
  [LOAD_CREDIT_CARDS_SUCCESS]: (state, action) => {
    const { creditCards } = action.payload
    return {
      ...state,
      creditCards,
      isCreditCardsLoading: false,
    }
  },
}

// ------------------------------------
// Reducer
// ------------------------------------
const initialState = {
  creditCards: [],
  delivery: {},
}

export default function checkout(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}

// ------------------------------------
// Sagas
// ------------------------------------

// Apply coupon and apply credit sagas have a try/catch block in them with an empty catch in order to catch the
// thrown SubmissionError needed for Redux-form to understand that remote submission failed. Since these are not
// being called within a form and called with formSaga, the uncaught error blows everything up.

export function* watchApplyCoupon() {
  while (true) {
    const {
      payload: { couponCode },
    } = yield take(APPLY_COUPON)
    const { dropoffId } = yield select(selectCurrentDeliveryDropoff)
    try {
      yield call(
        apiSaga,
        submitCoupon,
        [dropoffId, couponCode],
        applyCouponSuccess,
        applyCouponFailure,
      )
      // eslint-disable-next-line no-empty
    } catch (error) {}
  }
}

export function* watchApplyCredit() {
  while (true) {
    const {
      payload: { creditAmount },
    } = yield take(APPLY_CREDIT)
    const { dropoffId } = yield select(selectCurrentDeliveryDropoff)
    try {
      yield call(
        apiSaga,
        submitCredit,
        [dropoffId, creditAmount],
        applyCreditSuccess,
        applyCreditFailure,
      )
      // eslint-disable-next-line no-empty
    } catch (error) {}
  }
}

export function* watchApplyCouponOrCreditSuccess() {
  while (true) {
    try {
      const [payloadCoupon, payloadCredit] = yield race([take(APPLY_COUPON), take(APPLY_CREDIT)])
      const skipDeliveryCalc =
        payloadCoupon?.payload?.skipDeliveryCalc || payloadCredit?.payload?.skipDeliveryCalc

      yield race([take(APPLY_COUPON_SUCCESS), take(APPLY_CREDIT_SUCCESS)])
      const { dropoffId } = yield select(selectCurrentDeliveryDropoff)
      yield put(loadCheckoutCartStart(dropoffId, skipDeliveryCalc))
    } catch (error) {
      console.error(error)
    }
  }
}

export function* watchLoadCreditCards() {
  while (true) {
    yield take(LOAD_CREDIT_CARDS)
    yield call(apiSaga, loadCreditCards, [], loadCreditCardsSuccess, loadCreditCardsFailure)
  }
}

export function buildNewCardCheckoutRequest(checkoutData) {
  return new Promise((resolve, reject) => {
    if (!window.Heartland) {
      const ex = new Error(HEARTLAND_ERROR)
      logException(ex)
      reject(ex)
    }

    const { cvv, expMonth, expYear, number, zip } = checkoutData.cc
    const hps = new window.Heartland.HPS({
      cardCvv: cvv,
      cardExpMonth: expMonth.toString(),
      cardExpYear: expYear.toString(),
      cardNumber: number,
      error: resp => {
        logException(`Error processing Heartland token request: ${resp.error.message}`)
        reject(resp.error)
      },
      publicKey: process.env.REACT_APP_HEARTLAND_PUBLIC_KEY,
      success: resp => {
        /* eslint-disable camelcase */
        const { exp_month, exp_year, last_four, token_value } = resp
        const request = {
          ...omit(checkoutData, ['cc', 'SelectedCard']),
          IsNewCard: true,
          SSToken: token_value,
          expMonth: exp_month,
          expYear: exp_year,
          lastFour: last_four,
          zip: zip,
        }
        /* eslint-enable camelcase */
        resolve(request)
      },
    })
    hps.tokenize()
  })
}

export function* watchCheckout() {
  while (true) {
    try {
      const {
        payload: { checkoutData },
      } = yield take(CHECKOUT)
      const { dropoffId } = yield select(selectCurrentDeliveryDropoff)
      const { orderTotal } = yield select(selectOrderTotal)
      let request = { ...checkoutData }
      if (orderTotal === 0 && request.SelectedCard === -1) {
        request.SelectedCard = null
      }
      if (request.SelectedCard !== undefined) {
        if (request.SelectedCard >= 0) {
          request.IsNewCard = false
        } else {
          request = yield call(buildNewCardCheckoutRequest, checkoutData)
        }
      } else {
        // wipe everything out but the user, since the meal is free
        request = {
          User: { ...checkoutData.User },
        }
      }
      request.dropoffId = dropoffId
      request.FirstName = request.User.firstName
      request.LastName = request.User.lastName
      // Update the user first
      yield put(
        updatePersonalInfoStart({
          firstName: checkoutData.User.firstName,
          lastName: checkoutData.User.lastName,
          phone: checkoutData.User.phone,
        }),
      )
      yield call(formSaga, 'checkout', checkoutOrder, [request], checkoutSuccess, checkoutFailure)
    } catch (ex) {
      yield put(checkoutFailure(ex))
    }
  }
}

export function* watchCheckoutSuccess() {
  while (true) {
    const {
      payload: { orderInfo },
    } = yield take(CHECKOUT_SUCCESS)
    resetCaches()
    const checkoutCart = yield select(selectOrderTotal)
    const { deliveryDateTime, storeId, storeName } = yield select(selectCurrentDeliveryDropoff)
    const locationId = yield select(selectLocationDeliveryId)
    if (window.appboy) {
      setTimeout(() => window.appboy.requestContentCardsRefresh(), 5000)
    }
    const { couponCode } = yield select(selectCouponUsage)

    try {
      pushAnalyticsEvent('Ordering', 'Checked Out', undefined, checkoutCart.orderId)
      pushGTMDataLayer({
        event: 'purchase',
        orderAmount: checkoutCart.itemSubTotal,
        orderCouponAmount: checkoutCart.couponSubTotal,
        orderCouponName: couponCode,
        orderCredits: checkoutCart.orderCreditSubTotal,
        orderDeliveryDaysFromToday: differenceInCalendarDays(deliveryDateTime, new Date()),
        orderDeliveryFee: checkoutCart.foodsbyFee,
        orderId: checkoutCart.orderId,
        orderRestaurantId: storeId,
        orderRestaurantName: storeName,
        orderSubtotal: checkoutCart.preTaxSubTotal,
        orderTax: checkoutCart.taxSubTotal,
        type: 'single',
      })
    } catch (ex) {
      logException(ex)
    }
    yield put(resetSubscriptionState())
    yield put(
      push(
        formatUrl(confirmationRoute.path, {
          locationId: locationId,
          orderId: orderInfo.orderId,
        }),
      ),
    )
  }
}

export function* watchLoadingPrograms() {
  while (true) {
    try {
      const {
        payload: { date },
      } = yield take(LOAD_AVAILABLE_PROGRAM)
      yield call(
        apiSaga,
        getProgramForUser,
        [date],
        loadAvailableProgramSuccess,
        loadAvailableProgramFailure,
      )
    } catch (ex) {
      yield put(loadAvailableProgramFailure(ex))
    }
  }
}

export const getProgramForUser = date =>
  api.get(`/api/v2/accounts/programs/best-available?date=${date}`)
